import * as angular from "angular";
import {
	createDefaultValue, createLabelDescriptionFrom
} from "@jsonforms/core";
import { set } from "lodash";
import {
	arrayItemSchema,
	cloneValue,
	collectionDraftErrors,
	controlPath,
	dataAtPath,
	enumOptions,
	FormRuntime,
	JsonSchemaDefinition,
	FormUiSchema,
	generatedUiSchema,
	isRecord,
	resolveSchema,
	schemaType
} from "../runtime";
import type { ControlState } from "./control-state";
import {
	clearHiddenTimeZones,
	hasPendingEdits
} from "./form-session";
import validationMessages from "./validation-messages";
import type {
	FormSession,
	PendingFormEdit
} from "./form-session";
import {
	formTimeZone,
	TimeZoneAppState
} from "./time-zone-context";
import template from "./array-control.html";
import referenceLabels from "./reference-labels";
import { referenceLabelKey } from "./reference-label-store";
import { pointerSegment } from "../schema";
import { hasInvalidVisibleInput } from "./native-validation";
import { dateDisplayValue } from "../dates";
import { decodeFileValue } from "../files";

interface ArrayColumn {
	key: string;
	title: string;
	schema: JsonSchemaDefinition;
	required?: boolean;
	ui?: FormUiSchema;
}

interface ReferenceColumn {
	key: string;
	schema: JsonSchemaDefinition;
	ui: FormUiSchema;
}

export interface ArrayControlController {
	control: ControlState;
}

// AngularJS owns the merged binding properties.
// eslint-disable-next-line no-redeclare
export class ArrayControlController implements angular.IController {
	public static $inject = ["appState", "$element", "$userProfile", "$scope"];

	public rows: unknown[] = [];

	public columns: ArrayColumn[] = [];

	public referenceColumns: ReferenceColumn[] = [];

	public draft?: FormSession;

	public draftSchema?: JsonSchemaDefinition;

	public draftUi?: FormUiSchema;

	public draftPath = "";

	public draftErrors: string[] = [];

	public draftForm?: angular.IFormController;

	private parentRuntime?: FormRuntime;

	private parentPath?: string;

	private draftIndex = 0;

	private isNewDraft = false;

	private registeredSession?: FormSession;

	private removeValidationWatch?: () => void;

	private readonly pendingEdit: PendingFormEdit = {
		isActive: () => Boolean(this.draft && !this.draft.readonly
			&& this.parentRuntime === this.control.session.runtime && this.control.canEdit())
	};

	public constructor(
		private appState: TimeZoneAppState,
		private $element: angular.IAugmentedJQuery,
		private $userProfile: { getUserProfile(): { timeDisplay?: string } },
		private $scope: angular.IScope
	) {}

	public $onInit(): void {
		this.removeValidationWatch = this.$scope.$watchGroup([
			() => this.draft?.showErrors,
			() => this.draft?.runtime.errors,
			() => this.draftForm?.$valid,
			() => this.draft?.pendingEdits?.size ?? 0
		], () => this.refreshDraftErrors());
	}

	public $onChanges(): void {
		const value = dataAtPath(this.control.session.runtime.data, this.control.path);
		this.rows = Array.isArray(value) ? value : [];

		const item = this.itemSchema(0);
		const itemPath = [this.control.path, 0].filter(segment => segment !== "").join(".");
		const alternative = this.control.session.runtime.getAlternatives(item, itemPath);
		this.columns = schemaType(item) === "object" && !alternative
			? Object.entries(item.properties ?? {}).map(([key, value]) => {
				const schema = resolveSchema(this.control.session.runtime.schema, value);
				const label = createLabelDescriptionFrom({
					type: "Control",
					scope: "#/properties/" + pointerSegment(key)
				}, schema);

				return {
					key,
					title: label.text,
					required: item.required?.includes(key),
					schema
				};
			})
			: [{
				key: "",
				title: item.title ?? "Value",
				schema: item
			}];

		for(const column of this.columns) {
			column.ui = this.referenceUi(column.key);
		}

		const referenceColumns = new Map<string, ReferenceColumn>();

		this.rows.forEach((_row, index) => {
			for(const column of this.columns) {
				if(this.isReference(column, index)) {
					const schema = this.columnSchema(column, index);
					const ui = column.ui ?? this.control.ui;
					const key = referenceLabelKey(schema, ui);
					referenceColumns.set(key, { key,
						schema,
						ui });
				}
			}
		});
		this.referenceColumns = [...referenceColumns.values()];

		this.refreshDraftContext();
	}

	public canAdd(): boolean {
		const maximum = this.control.schema.maxItems;

		return !this.draft && this.control.canEdit() && (maximum === undefined || this.rows.length < maximum);
	}

	public canRemove(): boolean {
		return this.control.canEdit() && this.rows.length > 0;
	}

	public remove(index: number): void {
		if(this.canRemove()) {
			if(this.draft) {
				if(!this.isNewDraft && index === this.draftIndex) {
					this.cancel();
				} else {
					this.draft.runtime.removeRow(this.control.path, index);
					this.draft.timeZones.removeRow(this.control.path, index);

					if(index < this.draftIndex) {
						this.draftIndex--;
					}

					this.refreshDraftPresentation();
				}
			}

			this.control.session.timeZones.removeRow(this.control.path, index);
			this.control.session.runtime.alternatives.removeRow(this.control.path, index);
			this.control.change(this.rawRows().filter((_, item) => item !== index));
		}
	}

	public edit(index?: number): void {
		if(this.draft || !this.control.isVisible() || (index === undefined && !this.canAdd())) {
			return;
		}

		const runtime = this.control.session.runtime;
		this.parentRuntime = runtime;
		this.parentPath = this.control.path;
		this.draftIndex = index ?? this.rows.length;
		this.isNewDraft = index === undefined;
		this.refreshDraftPresentation();

		const draftRuntime = new FormRuntime(runtime.schema, cloneValue(runtime.data), runtime.ui, runtime.alternatives);

		if(index === undefined) {
			const value: unknown = createDefaultValue(this.draftSchema ?? {}, runtime.schema);
			draftRuntime.update(this.control.path, [...this.rawRows(), value]);
		}

		this.draftErrors = [];
		this.draft = {
			runtime: draftRuntime,
			timeZones: this.control.session.timeZones.clone(),
			referenceLabels: this.control.session.referenceLabels,
			revision: 0,
			readonly: !this.control.canEdit(),
			showErrors: false,
			pendingEdits: new Set(),
			update: (path, value) => {
				if(!this.draft || this.draft.readonly) {
					return;
				}

				draftRuntime.update(path, value);
				clearHiddenTimeZones(this.draft);
				this.draft.revision++;
			}
		};
		clearHiddenTimeZones(this.draft);
		this.registerPendingEdit();
	}

	public cancel(): void {
		this.registeredSession?.pendingEdits?.delete(this.pendingEdit);
		this.registeredSession = undefined;
		this.draft = undefined;
		this.draftErrors = [];
		this.parentRuntime = undefined;
		this.parentPath = undefined;
	}

	public $onDestroy(): void {
		this.removeValidationWatch?.();
		this.cancel();
	}

	public save(): void {
		this.refreshDraftContext();

		if(!this.draft || !this.control.canEdit()) {
			return;
		}

		this.draft.showErrors = true;
		this.draft.revision++;
		this.refreshDraftErrors();

		if(this.draftErrors.length) {
			return;
		}

		const value = this.rawRows().map(cloneValue);
		const item = cloneValue(dataAtPath(this.draft.runtime.data, this.draftPath));

		if(this.isNewDraft) {
			value.splice(this.draftIndex, 0, item);
		} else {
			value.splice(this.draftIndex, 1, item);
		}

		this.control.session.timeZones.replaceSubtree(this.draftPath, this.draft.timeZones);
		this.control.session.runtime.alternatives.replaceSubtree(this.draftPath, this.draft.runtime.alternatives);
		this.control.change(value);
		this.cancel();
	}

	public summary(row: unknown, column: ArrayColumn, rowIndex: number): string {
		const value = column.key ? (isRecord(row) ? row[column.key] : undefined) : row;
		const schema = this.columnSchema(column, rowIndex);
		const type = schemaType(schema);

		if(value == null) {
			return "—";
		}

		const option = enumOptions(schema).find(item => angular.equals(item.value, value));

		if(option) {
			return option.label;
		}

		if(this.isReference(column, rowIndex)) {
			const key = referenceLabelKey(schema, column.ui ?? this.control.ui);

			return this.control.session.referenceLabels?.get(key)?.get(value) ?? "—";
		}

		if(type === "string" && (schema.format === "base64" || schema.format === "file")) {
			return decodeFileValue(value)?.name || "File selected";
		}

		if(type === "string" && (schema.format === "date" || schema.format === "date-time")) {
			const path = [this.control.path, rowIndex].filter(segment => segment !== "").join(".");
			const form = this.appState.forms?.selected;
			const mode = schema.metadata?.timeZoneMode ?? form?.TimeMode?.Code;
			const entityTimeZone = this.control.session.timeZones.getOwn(path);
			const preserveOffset = mode === "BYENTITY" && entityTimeZone === undefined;
			const timeZone = formTimeZone(schema, form, entityTimeZone);

			return dateDisplayValue(value, schema, timeZone, preserveOffset, this.$userProfile.getUserProfile().timeDisplay !== "12h") || "—";
		}

		if(typeof value === "boolean") {
			return value ? "Yes" : "No";
		}

		return typeof value === "object" ? Array.isArray(value) ? `${value.length} items` : "Details" : String(value);
	}

	public isReference(column: ArrayColumn, rowIndex: number): boolean {
		const schema = this.columnSchema(column, rowIndex);
		const type = schemaType(schema);

		return enumOptions(schema).length === 0
			&& (type === "number" || type === "integer")
			&& (schema.format === "entity" || schema.format === "datapoint");
	}

	public columnSchema(column: ArrayColumn, rowIndex: number): JsonSchemaDefinition {
		const runtime = this.control.session.runtime;
		const path = [this.control.path, rowIndex, column.key].filter(segment => segment !== "").join(".");
		let schema = resolveSchema(runtime.schema, column.schema);
		const visited = new Set<JsonSchemaDefinition>();

		while(!visited.has(schema)) {
			visited.add(schema);
			const alternative = runtime.getAlternatives(schema, path);
			const selected = alternative?.options[alternative.index];

			if(!selected) {
				break;
			}

			schema = selected.schema;
		}

		return schema;
	}

	private referenceUi(key: string): FormUiSchema {
		const find = (ui: FormUiSchema): FormUiSchema | undefined => {
			if(ui.type === "Control" && controlPath(ui.scope ?? "#") === key) {
				return ui;
			}

			for(const element of ui.elements ?? []) {
				const match = find(element);

				if(match) {
					return match;
				}
			}

			return ui.options?.detail ? find(ui.options.detail) : undefined;
		};
		const detail = this.control.ui.options?.detail;

		return (detail && find(detail)) ?? this.control.ui;
	}

	private itemSchema(index: number): JsonSchemaDefinition {
		return arrayItemSchema(this.control.session.runtime.schema, this.control.schema, index);
	}

	private refreshDraftErrors(): void {
		if(!this.draft?.showErrors) {
			this.draftErrors = [];
			return;
		}

		this.draftErrors = collectionDraftErrors(
			this.draft.runtime.errors,
			this.control.path,
			this.draftPath,
			this.draft.runtime.schema,
			this.draft.runtime.data,
			this.draft.runtime.inputErrors
		);

		if(hasPendingEdits(this.draft)) {
			this.draftErrors.push("Save or cancel the open entry before continuing.");
		}

		if(this.draftForm?.$valid === false && hasInvalidVisibleInput(this.$element[0])) {
			this.draftErrors = [...this.draftErrors, "Please correct the highlighted fields."];
		}
	}

	private refreshDraftContext(): void {
		if(!this.draft) {
			return;
		}

		const session = this.control.session;

		if(this.parentRuntime !== session.runtime) {
			this.cancel();
			return;
		}

		const item = cloneValue(dataAtPath(this.draft.runtime.data, this.draftPath));

		if(this.parentPath !== this.control.path) {
			if(!this.rebaseDraftPath()) {
				this.cancel();
				return;
			}

			this.parentPath = this.control.path;
			this.refreshDraftPresentation();
		}

		const rows = this.rawRows().map(cloneValue);

		if(this.draftIndex > rows.length || (!this.isNewDraft && this.draftIndex === rows.length)) {
			this.cancel();
			return;
		}

		if(this.isNewDraft) {
			rows.splice(this.draftIndex, 0, item);
		} else {
			rows.splice(this.draftIndex, 1, item);
		}

		const alternatives = this.draft.runtime.alternatives.clone();
		this.draft.runtime.alternatives.replaceSubtree("", session.runtime.alternatives);
		this.draft.runtime.alternatives.replaceSubtree(this.draftPath, alternatives);

		const parentData = cloneValue(session.runtime.data);
		const data = this.control.path
			? set(isRecord(parentData) || Array.isArray(parentData) ? parentData : {}, this.control.path, rows)
			: rows;

		// A context refresh preserves the draft's file attempts; it is not a value edit.
		this.draft.runtime.replace(data, true);

		const timeZones = session.timeZones.clone();
		timeZones.replaceSubtree(this.draftPath, this.draft.timeZones);
		this.draft.timeZones = timeZones;
		clearHiddenTimeZones(this.draft);
		this.draft.readonly = !this.control.canEdit();
		this.draft.revision++;
		this.registerPendingEdit();
	}

	private refreshDraftPresentation(): void {
		this.draftSchema = this.itemSchema(this.draftIndex);
		this.draftUi = this.control.ui.options?.detail
			?? generatedUiSchema(this.draftSchema, this.control.session.runtime.schema);
		this.draftPath = [this.control.path, this.draftIndex].filter(value => value !== "").join(".");
	}

	private rebaseDraftPath(): boolean {
		if(!this.draft || this.parentPath === undefined) {
			return false;
		}

		const previous = this.parentPath.split(".");
		const current = this.control.path.split(".");
		const changed = previous.findIndex((segment, index) => segment !== current[index]);

		if(previous.length !== current.length || changed < 0
			|| !/^\d+$/.test(previous[changed]) || !/^\d+$/.test(current[changed])
			|| previous.slice(changed + 1).join(".") !== current.slice(changed + 1).join(".")) {
			return false;
		}

		const oldIndex = Number(previous[changed]);
		const newIndex = Number(current[changed]);
		const ancestorPath = current.slice(0, changed).join(".");

		if(oldIndex <= newIndex) {
			return false;
		}

		for(let index = newIndex; index < oldIndex; index++) {
			this.draft.runtime.removeRow(ancestorPath, newIndex);
			this.draft.timeZones.removeRow(ancestorPath, newIndex);
		}

		return true;
	}

	private registerPendingEdit(): void {
		const session = this.control.session;

		if(this.registeredSession !== session || !this.draft || this.draft.readonly) {
			this.registeredSession?.pendingEdits?.delete(this.pendingEdit);
			this.registeredSession = undefined;
		}

		if(this.draft && !this.draft.readonly) {
			const edits = session.pendingEdits ?? new Set<PendingFormEdit>();
			session.pendingEdits = edits;
			edits.add(this.pendingEdit);
			this.registeredSession = session;
		}
	}

	private rawRows(): unknown[] {
		return Array.isArray(this.control.value) ? this.control.value : [];
	}
}

export default angular.module("vesl.extensions.json-schema.array-control", [referenceLabels, validationMessages])
	.component("jsonSchemaArrayControl", {
		template,
		controller: ArrayControlController,
		bindings: { control: "<" }
	})
	.name;
